import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { EventCategory } from '@/types/event-category';

export const useCategories = (includeInactive = false, targetAudience?: string) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['categories', includeInactive, targetAudience],
    queryFn: async () => {
      let query = supabase
        .from('event_categories')
        .select('*')
        .order('order', { ascending: true });

      // Pour l'admin, on peut inclure les catégories inactives
      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      if (targetAudience) {
        query = query.eq('type_categorie', targetAudience as 'particulier' | 'professionnel');
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching categories:', error);
        throw error;
      }

      // Cast the data to match EventCategory type
      const categories: EventCategory[] = (data || []).map(item => ({
        ...item,
        type_categorie: item.type_categorie as 'particulier' | 'professionnel'
      }));

      return categories;
    },
  });

  const createCategoryMutation = useMutation({
    mutationFn: async (categoryData: Omit<EventCategory, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('event_categories')
        .insert(categoryData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Catégorie créée avec succès');
    },
    onError: (error) => {
      console.error('Error creating category:', error);
      toast.error('Erreur lors de la création de la catégorie');
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: async (category: EventCategory) => {
      // Exclure les champs auto-gérés par la DB
      const { id, created_at, updated_at, ...updateData } = category;
      
      const { data, error } = await supabase
        .from('event_categories')
        .update(updateData)
        .eq('id', category.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Catégorie mise à jour avec succès');
    },
    onError: (error) => {
      console.error('Error updating category:', error);
      toast.error('Erreur lors de la mise à jour de la catégorie');
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (categoryId: string) => {
      const { error } = await supabase
        .from('event_categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Catégorie supprimée avec succès');
    },
    onError: (error) => {
      console.error('Error deleting category:', error);
      toast.error('Erreur lors de la suppression de la catégorie');
    },
  });

  const reorderCategoriesMutation = useMutation({
    mutationFn: async (categories: EventCategory[]) => {
      const updates = categories.map((category, index) => ({
        id: category.id,
        order: index + 1
      }));

      const { error } = await supabase
        .from('event_categories')
        .update({ order: updates[0].order })
        .eq('id', updates[0].id);

      if (error) throw error;

      // Update all categories one by one to avoid the upsert issue
      for (const update of updates.slice(1)) {
        const { error } = await supabase
          .from('event_categories')
          .update({ order: update.order })
          .eq('id', update.id);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Ordre des catégories mis à jour');
    },
    onError: (error) => {
      console.error('Error reordering categories:', error);
      toast.error('Erreur lors de la réorganisation');
    },
  });

  return {
    categories: query.data,
    isLoading: query.isLoading,
    error: query.error,
    createCategory: createCategoryMutation.mutateAsync,
    updateCategory: updateCategoryMutation.mutateAsync,
    deleteCategory: deleteCategoryMutation.mutateAsync,
    reorderCategories: reorderCategoriesMutation.mutateAsync,
  };
};
